import { z } from "zod";
import {
    isValidTime12,
    time12ToMinutes,
} from "../../Patients/components/clinicalForms/nurseNotes/nurseVisitCommon";

const NAME_REGEX = /^[^0-9]*$/;

const nameValidator = (msg = "Name cannot contain digits") => z.string().regex(NAME_REGEX, msg);

// --- Route Sheet Schemas ---

export const routeSheetEntrySchema = z.object({
    entryNumber: z.coerce.number().min(1).max(10),
    visitDate: z.string().min(1, "Visit date is required"),
    patientSignature: z.string().optional(),
    patientSignatureUrl: z.string().optional().nullable(),
    patientSignatureImage: z.string().optional().nullable(),
    patientSignatureS3Key: z.string().optional().nullable(),
    patientSignatureS3Url: z.string().optional().nullable(),
    clearPatientSignature: z.boolean().optional(),
    visitCode: z.string().min(1, "Visit code is required").refine(
        (val) => ["E", "RV", "IV", "RC", "RE", "DC", "Ref", "NF", "S", "O", "IS"].includes(val),
        { message: "Invalid Visit Code" }
    ),
    timeIn: z.string().optional(),
    timeOut: z.string().optional(),
    mileage: z.coerce.number().nonnegative().optional(),
});

export const routeSheetSchema = z.object({
    tenantId: z.coerce.number().positive(),
    userId: z.coerce.number().positive().optional(),
    employeeName: nameValidator().min(1, "Employee name is required"),
    applicantName: nameValidator().nullable().optional(),
    title: z.string().optional(),
    patientName: nameValidator().optional(),
    mrNumber: z.string().optional(),
    applicantSignature: z.string().min(1, "Signature is required"),
    applicantSignedAt: z.string().optional(),
    entries: z.array(routeSheetEntrySchema),
});

// --- Patient Consent Schemas ---

export const patientConsentSchema = z.object({
    tenantId: z.coerce.number().positive(),
    userId: z.coerce.number().positive().optional(),
    patientId: z.coerce.number().positive().optional(),
    employeeName: nameValidator().min(1, "Employee name is required"),
    applicantName: nameValidator().nullable().optional(),

    // Consent Sections
    consentAdmissionTreatment: z.boolean().default(false),
    consentAuthorizationReleaseInfo: z.boolean().default(false),
    consentPatientCertificationPayment: z.boolean().default(false),
    consentHomeHealthBillingPps: z.boolean().default(false),
    consentAssignmentInsuranceBenefits: z.boolean().default(false),
    consentCmsAdvanceBeneficiaryNotice: z.boolean().default(false),

    // Payment Options
    paymentPrivateInsurance: z.boolean().default(false),
    paymentSelfPay: z.boolean().default(false),

    // Visit Frequency
    snVisits: z.string().nullable().optional(),
    chhaVisits: z.string().nullable().optional(),
    ptVisits: z.string().nullable().optional(),
    otVisits: z.string().nullable().optional(),
    stVisits: z.string().nullable().optional(),
    mswVisits: z.string().nullable().optional(),

    // Certification
    certPatientsRightsResponsibilities: z.boolean().default(false),
    certOasisPrivacyHippa: z.boolean().default(false),
    certStateJointCommissionHotline: z.boolean().default(false),
    certPatientsPrivacyRightsComplaint: z.boolean().default(false),
    certInitialPlanOfCareReviewed: z.boolean().default(false),
    certHomeHealthRatesReviewed: z.boolean().default(false),
    certStateJointCommissionHotlineInformed: z.boolean().default(false),
    certEmergencyPlanSafetyInstructions: z.boolean().default(false),
    certBrochureNonDiscriminatoryNotice: z.boolean().default(false),
    certReceivedCopyOfDocument: z.boolean().default(false),

    // Advance Directives
    advanceDirectives: z.string().nullable().optional(),

    // Photo Records Authorization
    photoRecordsAuthorization: z.boolean().default(false),

    // Signatures
    patientSignature: z.string().nullable().optional(),
    patientName: nameValidator().nullable().optional(),
    patientSignedAt: z.string().nullable().optional(),
    applicantSignature: z.string().min(1, "Signature is required"),
    applicantSignedAt: z.string().nullable().optional(),
});

// --- Medicare Non-Coverage Schemas ---

export const medicareNonCoverageSchema = z.object({
    tenantId: z.coerce.number().positive(),
    userId: z.coerce.number().positive().optional(),
    patientId: z.coerce.number().positive().optional(),
    employeeName: nameValidator().min(1, "Employee name is required"),
    patientName: nameValidator().min(1, "Patient name is required"),
    patientNumber: z.string().min(1, "Patient number is required"),
    effectiveDate: z.string().min(1, "Effective date is required"),
    serviceType: z.string().min(1, "Service type is required"),
    applicantName: nameValidator().nullable().optional(),
    applicantSignature: z.string().min(1, "Signature is required"),
    applicantSignedAt: z.string().optional(),
});

// --- Daily Log Schemas ---

export const dailyLogSchema = z.object({
    tenantId: z.coerce.number().positive().optional(),
    userId: z.coerce.number().positive().optional(),
    employeeName: nameValidator().min(1, "Employee name is required"),
    applicantName: nameValidator().nullable().optional(),
    title: z.string().min(1, "Role is required"),
    patientId: z
      .union([z.number().int().positive(), z.null()])
      .optional(),
    patientName: nameValidator().min(1, "Patient name is required"),
    serviceDate: z.string().optional().nullable(),
    timeIn: z.string().optional().nullable(),
    timeOut: z.string().optional().nullable(),
    dailyNotes: z.string().optional().nullable(),
    patientSignatureUrl: z.string().optional().nullable(),
    patientSignatureImage: z.string().optional().nullable(),
    patientSignatureS3Key: z.string().optional().nullable(),
    patientSignatureS3Url: z.string().optional().nullable(),
    clearPatientSignature: z.boolean().optional(),
    applicantSignature: z.string().optional().nullable(),
    applicantSignedAt: z.string().optional().nullable(),
    reviewedBySignature: z.string().optional().nullable(),
    reviewedBySignedAt: z.string().optional().nullable(),
    reviewedBySignedBy: z.string().optional().nullable(),
    comments: z.string().optional().nullable(),
}).superRefine((data, ctx) => {
    const inVal = String(data.timeIn || "").trim();
    const outVal = String(data.timeOut || "").trim();
    if (inVal && !isValidTime12(inVal)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["timeIn"],
            message: "Time In must be a valid time with AM or PM (e.g. 8:45 AM).",
        });
    }
    if (outVal && !isValidTime12(outVal)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["timeOut"],
            message: "Time Out must be a valid time with AM or PM (e.g. 9:30 AM).",
        });
    }
    if (inVal && outVal) {
        const start = time12ToMinutes(inVal);
        const end = time12ToMinutes(outVal);
        if (start != null && end != null && end <= start) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["timeOut"],
                message: "Time Out must be after Time In.",
            });
        }
    }
});
